import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigateTransition } from '../lib/navigation';
import IDCard from '../components/IDCard';
import LoadingView from '../components/LoadingView';
import { useFlow } from '../state/flow';
import { composeCard, composeShare } from '../lib/composeShare';
import { rasterizeLockup } from '../lib/photo';

// Minimum on-screen time for the loading screen — gives the fun-fact swap
// air time even on a fast connection where compose finishes immediately.
const MIN_MS = 5000;
// Hard ceiling — never trap the user here past this even if compose stalls.
// /welcome and /share have their own fallback paths to recover.
const MAX_MS = 12000;

export default function Loading() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigateTransition();
  const { photoDataUrl, setCardImage, setShareImages } = useFlow();
  const locale = i18n.resolvedLanguage ?? 'en';
  const cardRef = useRef<HTMLDivElement | null>(null);

  const facts = useMemo(() => t('loading.facts', { returnObjects: true }) as string[], [t]);
  // Gate the heavy IDCard mount until after the route's view-transition has
  // had a chance to play. IDCard fires a useLayoutEffect plus a useFitText
  // useLayoutEffect per <Field> (each iteratively reading scrollHeight /
  // scrollWidth, which force synchronous layout). Those run inside the
  // `flushSync(navigate)` step of `useNavigateTransition`, before the new
  // snapshot is captured — enough forced layouts there to starve the
  // compositor and make the 480ms /photo → /loading crossfade visibly drop.
  // Mounting IDCard one tick later on a setTimeout pushes that work past the
  // transition; the toPng pipeline waits on the same gate so we don't try to
  // rasterize a card that isn't in the DOM yet.
  const [cardReady, setCardReady] = useState(false);
  const [composeDone, setComposeDone] = useState(false);
  const [startTime] = useState(() => Date.now());

  useEffect(() => {
    const card = window.setTimeout(() => setCardReady(true), 500);
    return () => window.clearTimeout(card);
  }, []);

  // Advance to /welcome on whichever comes first: composition finishes (after
  // the minimum on-screen time has elapsed), or the hard MAX_MS ceiling.
  useEffect(() => {
    const elapsed = Date.now() - startTime;
    if (composeDone && elapsed >= MIN_MS) {
      navigate('/welcome');
      return;
    }
    const target = composeDone ? MIN_MS : MAX_MS;
    const id = window.setTimeout(() => navigate('/welcome'), target - elapsed);
    return () => window.clearTimeout(id);
  }, [composeDone, navigate, startTime]);

  // Render the lockup PNG (transparent bg with photo + text) and composite it
  // onto card.png + the two share BGs while the loading animation plays. The
  // lockup is rasterized at pixelRatio: 1 so its natural size matches the
  // 695×1037 stamp expected by composeCard / composeShare.
  useEffect(() => {
    if (!cardReady) return;
    let alive = true;
    void (async () => {
      try {
        if (!cardRef.current) return;
        const lockup = await rasterizeLockup(cardRef.current);
        if (!alive) return;
        const [card, a, b] = await Promise.all([
          composeCard(lockup, photoDataUrl),
          composeShare('3x4', lockup, locale, photoDataUrl),
          composeShare('9x16', lockup, locale, photoDataUrl),
        ]);
        if (!alive) return;
        setCardImage(card);
        setShareImages({ '3x4': a, '9x16': b });
      } catch {
        // /welcome and /share have their own fallback paths.
      } finally {
        if (alive) setComposeDone(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, [cardReady, setCardImage, setShareImages, locale, photoDataUrl]);

  return (
    <LoadingView title={t('loading.title')} subtitle={t('loading.subtitle')} facts={facts}>
      {cardReady && (
        <div className="loading__offscreen" aria-hidden="true">
          <IDCard ref={cardRef} />
        </div>
      )}
    </LoadingView>
  );
}
